import React, { useState, useEffect } from 'react';
import { apiService } from '@/services/api';

// §pharmacy-choice (branded weightloss): patient searches Beluga's pharmacy
// directory (proxied through the backend — POST /beluga/pharmacy-search →
// Beluga /external/pharmacies) and selects the retail pharmacy their branded
// prescription should be sent to. Beluga requires at least one of
// {name, city, state, zip}; returns up to 100 matches.
//
// Answer shape (stored + submitted):
//   { pharmacyId, storeName, address1, city, state, zip, phone }
//
// `searchDefaults` (from the shipping address) pre-seeds city/state/zip so the
// first search is one click for most patients.
// `hideContinue` — when embedded inline (e.g. inside the regular_rx checkout
// fork) the parent owns the advance button, so we suppress this step's own
// Continue. As a flow step (branded pharmacy-choice) it renders as before.
const PharmacySelectStep = ({ question, value, onAnswer, onNext, searchDefaults, hideContinue = false }) => {
  const [name, setName] = useState('');
  const [city, setCity] = useState(searchDefaults?.city || '');
  const [state, setState] = useState(searchDefaults?.state || '');
  const [zip, setZip] = useState(searchDefaults?.zip || '');
  const [results, setResults] = useState(null); // null = not searched yet
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState('');

  // Re-seed when the shipping address arrives after mount (back-nav etc.).
  useEffect(() => {
    if (!city && searchDefaults?.city) setCity(searchDefaults.city);
    if (!state && searchDefaults?.state) setState(searchDefaults.state);
    if (!zip && searchDefaults?.zip) setZip(searchDefaults.zip);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchDefaults?.city, searchDefaults?.state, searchDefaults?.zip]);

  const canSearch = Boolean(name.trim() || city.trim() || state.trim() || zip.trim());

  const runSearch = async () => {
    if (!canSearch || searching) return;
    setSearching(true);
    setError('');
    try {
      const res = await apiService.searchPharmacies({
        name: name.trim() || undefined,
        city: city.trim() || undefined,
        state: state.trim().toUpperCase() || undefined,
        zip: zip.trim() || undefined,
      });
      const list = Array.isArray(res?.data) ? res.data : Array.isArray(res?.pharmacies) ? res.pharmacies : [];
      setResults(list);
      if (list.length === 0) setError('No pharmacies matched your search. Try a broader search (e.g., just your ZIP code).');
    } catch (e) {
      console.error('Pharmacy search error:', e);
      setResults([]);
      setError(
        e?.response?.data?.msg ||
          e?.response?.data?.error ||
          'We could not search pharmacies right now. Please try again.',
      );
    } finally {
      setSearching(false);
    }
  };

  const selectPharmacy = (p) => {
    onAnswer({
      pharmacyId: p.PharmacyId,
      storeName: p.StoreName,
      address1: p.Address1 || '',
      city: p.City || '',
      state: p.State || '',
      zip: p.ZipCode || '',
      phone: p.PrimaryPhone || '',
    });
  };

  const isSelected = (p) => value && value.pharmacyId === p.PharmacyId;

  return (
    <div data-testid={`step-${question.id}`}>
      <h2 className="text-2xl md:text-3xl font-semibold text-center text-gray-900 mb-2">
        {question.question}
      </h2>
      {question.subtitle && (
        <p className="text-center text-gray-500 mb-6">{question.subtitle}</p>
      )}

      {/* Search controls */}
      <div className="bg-gray-50 rounded-2xl p-4 mb-5">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Pharmacy name (e.g., CVS)"
            className="form-input"
            data-testid="pharmacy-name-input"
          />
          <input
            type="text"
            value={city}
            onChange={(e) => setCity(e.target.value)}
            placeholder="City"
            className="form-input"
            data-testid="pharmacy-city-input"
          />
          <input
            type="text"
            value={state}
            onChange={(e) => setState(e.target.value.toUpperCase().slice(0, 2))}
            placeholder="State (e.g., TX)"
            maxLength={2}
            className="form-input"
            data-testid="pharmacy-state-input"
          />
          <input
            type="text"
            value={zip}
            onChange={(e) => setZip(e.target.value.replace(/[^0-9]/g, '').slice(0, 5))}
            placeholder="ZIP code"
            inputMode="numeric"
            className="form-input"
            data-testid="pharmacy-zip-input"
          />
        </div>
        <button
          type="button"
          onClick={runSearch}
          disabled={!canSearch || searching}
          className="btn-primary"
          data-testid="pharmacy-search-button"
        >
          {searching ? 'Searching…' : 'Search pharmacies'}
        </button>
        {!canSearch && (
          <p className="text-xs text-gray-400 mt-2">Enter at least one field to search.</p>
        )}
      </div>

      {error && (
        <p
          className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4"
          data-testid="pharmacy-search-error"
        >
          {error}
        </p>
      )}

      {/* Selected pharmacy (persists across back-nav even without re-search) */}
      {value && value.pharmacyId && (
        <div
          className="border-2 border-[#2d8a5f] bg-[#f0faf4] rounded-xl p-4 mb-4"
          data-testid="pharmacy-selected-summary"
        >
          <p className="text-sm font-semibold text-gray-900">{value.storeName}</p>
          <p className="text-sm text-gray-600">
            {[value.address1, value.city, value.state, value.zip].filter(Boolean).join(', ')}
          </p>
        </div>
      )}

      {/* Results */}
      {Array.isArray(results) && results.length > 0 && (
        <div className="space-y-3 mb-6 max-h-96 overflow-y-auto" data-testid="pharmacy-results">
          {results.map((p, index) => (
            <button
              key={p.PharmacyId || index}
              onClick={() => selectPharmacy(p)}
              className={`option-card w-full p-4 rounded-xl text-left transition-all ${
                isSelected(p) ? 'selected' : ''
              }`}
              data-testid={`pharmacy-option-${index}`}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="text-base font-medium text-gray-800 truncate">{p.StoreName}</p>
                  <p className="text-sm text-gray-500 truncate">
                    {[p.Address1, p.City, p.State, p.ZipCode].filter(Boolean).join(', ')}
                  </p>
                  {p.PrimaryPhone && <p className="text-xs text-gray-400">{p.PrimaryPhone}</p>}
                </div>
                <div
                  className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-all ${
                    isSelected(p) ? 'border-[#2d8a5f] bg-[#2d8a5f]' : 'border-gray-300'
                  }`}
                >
                  {isSelected(p) && (
                    <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                      <path
                        fillRule="evenodd"
                        d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                        clipRule="evenodd"
                      />
                    </svg>
                  )}
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {!hideContinue && (
        <button
          onClick={onNext}
          disabled={!value || !value.pharmacyId}
          className="btn-primary"
          data-testid="continue-button"
        >
          Continue
        </button>
      )}
    </div>
  );
};

export default PharmacySelectStep;
